import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router";

const useUpdateSearchParam = (name: string) => {
  const router = useRouter();
  // NOTE(sam): For some reason NextJS provides a separate declaration file for the navigation
  // module that marks these as nullable (only if used within the pages router) - VSCode doesn't
  // pick up on this but it blows up the NextJS build when you assume they aren't nullable. Since
  // we are using the app router now they should always return non-null values but I'd like to
  // figure out a better solution
  const pathname = usePathname()!;
  const searchParams = useSearchParams()!;
  const reactRouterHistory = useHistory();

  return useCallback(
    (values: string[]) => {
      const newParams = new URLSearchParams(searchParams.toString());
      // NextJS automatically populates this "legacy" param with segments of the current path
      // Removing it because otherwise it puts a bunch of garbage in the URL
      // TODO(sam): investigate further, might be resolved by switching to app router
      newParams.delete("legacy");
      newParams.delete(name);
      values.forEach(value => newParams.append(name, value));
      const searchString = newParams.toString();
      router.push(searchString ? `${pathname}?${newParams.toString()}` : pathname);
      // Manually sync query params to react router in case anything cares about them, it won't
      // pick them up automatically
      reactRouterHistory.push({ search: searchString && `?${searchString}` });
    },
    [name, router, pathname, searchParams, reactRouterHistory]
  );
};

export const useClearSearchParams = () => {
  const router = useRouter();
  const pathname = usePathname()!;

  return useCallback(() => {
    router.push(pathname);
  }, [router, pathname]);
};

export const useSearchParam = (name: string): [string | null, (val: string | null) => void] => {
  const searchParams = useSearchParams()!;
  const updateSearchParam = useUpdateSearchParam(name);

  return useMemo(
    () => [
      searchParams.get(name),
      (newValue: string | null) => {
        updateSearchParam(newValue ? [newValue] : []);
      },
    ],
    [searchParams, name, updateSearchParam]
  );
};

export const useArraySearchParam = (name: string) => {
  const searchParams = useSearchParams()!;
  const updateSearchParam = useUpdateSearchParam(name);

  const value = searchParams.getAll(name);

  return [value, updateSearchParam] as const;
};

export const useNumberSearchParam = (name: string) => {
  const [value, setValue] = useSearchParam(name);

  const numberValue = value ? parseInt(value, 10) : null;

  const setNumberValue = (newValue: number | null) => {
    setValue(newValue === null ? null : newValue.toString());
  };

  return [numberValue, setNumberValue] as const;
};

export const useBooleanSearchParam = (name: string) => {
  const [value, setValue] = useSearchParam(name);

  const booleanValue = value !== null ? value === "true" : null;

  const setBooleanValue = (newValue: boolean | null) => {
    setValue(newValue === null ? null : newValue.toString());
  };

  return [booleanValue, setBooleanValue] as const;
};

export const useNumberArraySearchParam = (name: string) => {
  const [value, setValue] = useArraySearchParam(name);

  const numberValue = value.map(v => parseInt(v, 10));

  const setNumberValue = (newValue: number[]) => {
    setValue(newValue.map(v => v.toString()));
  };

  return [numberValue, setNumberValue] as const;
};
